import { geocode } from '../../services/mapboxService';
import type { TrmIntake } from '../../functions/src/common/trmWorkflow';

export async function collectTrmResearch(intake: TrmIntake) {
    const locations: Array<{ city: string; country: string; latitude: number | null; longitude: number | null;
        hospitals: Array<{ name: string; address: string; url: string }>;
        pharmacies: Array<{ name: string; address: string; url: string }> }> = [];
    const sourceErrors: string[] = [];
    for (const stop of intake.stops) {
        const address = `${stop.city}, ${stop.country}`;
        const location = { city: stop.city, country: stop.country, latitude: null as number | null,
            longitude: null as number | null, hospitals: [] as Array<{ name: string; address: string; url: string }>,
            pharmacies: [] as Array<{ name: string; address: string; url: string }> };
        try {
            const coordinates = await geocode(address);
            location.latitude = coordinates.lat;
            location.longitude = coordinates.lng;
            if (!coordinates.isExact) sourceErrors.push(`${address}: map location is approximate.`);
            const library = await google.maps.importLibrary('places') as any;
            const result = await Promise.allSettled(([
                ['hospitals', 'hospital'], ['pharmacies', 'pharmacy'],
            ] as const).map(async ([key, type]) => {
                const response = await library.Place.searchNearby({ fields: ['displayName', 'formattedAddress', 'googleMapsURI'],
                    locationRestriction: { center: { lat: coordinates.lat, lng: coordinates.lng }, radius: 15000 },
                    includedPrimaryTypes: [type], maxResultCount: 3,
                    rankPreference: library.SearchNearbyRankPreference.DISTANCE });
                return { key, places: (response.places || []).map((place: any) => ({
                    name: String(place.displayName || ''), address: String(place.formattedAddress || ''),
                    url: String(place.googleMapsURI || ''),
                })) };
            }));
            for (let index = 0; index < result.length; index++) {
                const item = result[index];
                if (item.status === 'fulfilled') location[item.value.key] = item.value.places;
                else sourceErrors.push(`${address}: ${index ? 'pharmacy' : 'hospital'} search unavailable.`);
            }
        } catch {
            sourceErrors.push(`${address}: map or Places lookup unavailable.`);
        }
        locations.push(location);
    }
    const routes: Array<{ origin: string; destination: string; durationMinutes: number; distanceMeters: number }> = [];
    for (let index = 1; index < intake.stops.length; index++) {
        const origin = intake.stops[index - 1];
        const destination = intake.stops[index];
        if (origin.country !== destination.country) continue;
        try {
            const library = await google.maps.importLibrary('routes' as any) as any;
            const response = await library.Route.computeRoutes({ origin: `${origin.city}, ${origin.country}`,
                destination: `${destination.city}, ${destination.country}`, travelMode: 'DRIVING',
                routingPreference: 'TRAFFIC_AWARE', fields: ['distanceMeters', 'durationMillis'] });
            const route = response.routes?.[0];
            if (route?.distanceMeters && route?.durationMillis)
                routes.push({ origin: origin.city, destination: destination.city,
                    durationMinutes: Math.ceil(Number(route.durationMillis) / 60000),
                    distanceMeters: Number(route.distanceMeters) });
            else sourceErrors.push(`${origin.city} to ${destination.city}: driving route unavailable.`);
        } catch {
            sourceErrors.push(`${origin.city} to ${destination.city}: driving route unavailable.`);
        }
    }
    return { locations, routes, sourceErrors, gatheredAt: new Date().toISOString() };
}
